import { useState } from "react"
import { isWebsocketUrl, normalizeRelayUrl, uniqueRelayUrls } from "@/utils/url"

export interface AddRelayFormProps {
  onAdd: (urls: string[]) => Promise<void> | void
  placeholder?: string
  buttonLabel?: string
  autoFocus?: boolean
  compact?: boolean
}

/**
 * Relay address input. Accepts `relay.example.com` as well as full `wss://`
 * URLs, normalises them, reports invalid entries and never submits zero relays.
 */
export function AddRelayForm({
  onAdd,
  placeholder = "wss://relay.example.com",
  buttonLabel = "Add relay",
  autoFocus,
  compact,
}: AddRelayFormProps) {
  const [value, setValue] = useState("")
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    const parts = value
      .split(/[\s,]+/)
      .map((part) => part.trim())
      .filter(Boolean)
    if (parts.length === 0) return

    const invalid = parts.filter((part) => !isWebsocketUrl(part))
    const urls = uniqueRelayUrls(parts)
    if (urls.length === 0) {
      setError(`“${parts[0]}” is not a valid relay address (expected ws:// or wss://)`)
      return
    }
    if (invalid.length > 0) {
      setError(`Ignored ${invalid.length} invalid address${invalid.length === 1 ? "" : "es"}: ${invalid.join(", ")}`)
    } else {
      setError(undefined)
    }

    setBusy(true)
    try {
      await onAdd(urls)
      setValue("")
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className={`add-relay-form ${compact ? "add-relay-form-compact" : ""}`}
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <div className="add-relay-row">
        <input
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
            setError(undefined)
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          spellCheck={false}
          aria-label="Relay address"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "add-relay-error" : undefined}
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !value.trim()}>
          {buttonLabel}
        </button>
      </div>
      {error && (
        <p className="form-error" id="add-relay-error" role="alert">
          {error}
        </p>
      )}
      <p className="muted small">
        Tip: you can paste several relays at once. Normalised to{" "}
        <code>{normalizeRelayUrl("relay.example.com") ?? "wss://relay.example.com/"}</code>.
      </p>
    </form>
  )
}
